/** Tipos compartilhados pelo módulo de Departamento Pessoal. */

export type DpEmployeeStatus = "ATIVO" | "AFASTADO" | "FERIAS" | "DESLIGADO";
export type DpGender = "MASCULINO" | "FEMININO" | "OUTRO" | "NAO_INFORMADO";
export type DpMaritalStatus =
  | "SOLTEIRO"
  | "CASADO"
  | "DIVORCIADO"
  | "VIUVO"
  | "UNIAO_ESTAVEL"
  | "OUTRO";
export type DpBankAccountType = "CORRENTE" | "POUPANCA" | "SALARIO" | "PAGAMENTO";
export type DpDependentRelationship =
  | "FILHO"
  | "ENTEADO"
  | "CONJUGE"
  | "COMPANHEIRO"
  | "PAI"
  | "MAE"
  | "IRMAO"
  | "TUTELADO"
  | "OUTRO";
export type DpContractType =
  | "CLT_INDETERMINADO"
  | "CLT_DETERMINADO"
  | "CLT_INTERMITENTE"
  | "APRENDIZ"
  | "ESTAGIO";
export type DpSalaryBasis = "MENSAL" | "HORA";
export type DpContractChangeReason =
  | "ADMISSAO"
  | "REAJUSTE"
  | "PROMOCAO"
  | "TRANSFERENCIA"
  | "ALTERACAO_JORNADA"
  | "ALTERACAO_CARGO"
  | "OUTRO";

export type DpAllowanceBase = "SALARIO_MINIMO" | "SALARIO_CONTRATUAL" | "VALOR_INFORMADO";
export type DpLegalParameterType =
  | "SALARIO_MINIMO"
  | "SALARIO_FAMILIA_TETO"
  | "SALARIO_FAMILIA_COTA";

export type DpPaginated<T> = {
  page: number;
  limit: number;
  total: number;
  items: T[];
};

export const EMPLOYEE_STATUS_LABELS: Record<DpEmployeeStatus, string> = {
  ATIVO: "Ativo",
  AFASTADO: "Afastado",
  FERIAS: "Férias",
  DESLIGADO: "Desligado",
};

export const GENDER_LABELS: Record<DpGender, string> = {
  MASCULINO: "Masculino",
  FEMININO: "Feminino",
  OUTRO: "Outro",
  NAO_INFORMADO: "Não informado",
};

export const MARITAL_STATUS_LABELS: Record<DpMaritalStatus, string> = {
  SOLTEIRO: "Solteiro(a)",
  CASADO: "Casado(a)",
  DIVORCIADO: "Divorciado(a)",
  VIUVO: "Viúvo(a)",
  UNIAO_ESTAVEL: "União estável",
  OUTRO: "Outro",
};

export const BANK_ACCOUNT_TYPE_LABELS: Record<DpBankAccountType, string> = {
  CORRENTE: "Conta corrente",
  POUPANCA: "Conta poupança",
  SALARIO: "Conta salário",
  PAGAMENTO: "Conta de pagamento",
};

export const DEPENDENT_RELATIONSHIP_LABELS: Record<DpDependentRelationship, string> = {
  FILHO: "Filho(a)",
  ENTEADO: "Enteado(a)",
  CONJUGE: "Cônjuge",
  COMPANHEIRO: "Companheiro(a)",
  PAI: "Pai",
  MAE: "Mãe",
  IRMAO: "Irmão/Irmã",
  TUTELADO: "Tutelado(a)",
  OUTRO: "Outro",
};

export const CONTRACT_TYPE_LABELS: Record<DpContractType, string> = {
  CLT_INDETERMINADO: "CLT — prazo indeterminado",
  CLT_DETERMINADO: "CLT — prazo determinado",
  CLT_INTERMITENTE: "CLT — intermitente",
  APRENDIZ: "Aprendiz",
  ESTAGIO: "Estágio",
};

/** Tipos de contrato que exigem data de término — espelha a regra do backend. */
export const CONTRACT_TYPES_WITH_FIXED_TERM: DpContractType[] = [
  "CLT_DETERMINADO",
  "APRENDIZ",
  "ESTAGIO",
];

export const SALARY_BASIS_LABELS: Record<DpSalaryBasis, string> = {
  MENSAL: "Mensal",
  HORA: "Por hora",
};

export const CONTRACT_CHANGE_REASON_LABELS: Record<DpContractChangeReason, string> = {
  ADMISSAO: "Admissão",
  REAJUSTE: "Reajuste",
  PROMOCAO: "Promoção",
  TRANSFERENCIA: "Transferência",
  ALTERACAO_JORNADA: "Alteração de jornada",
  ALTERACAO_CARGO: "Alteração de cargo",
  OUTRO: "Outro",
};

/**
 * Motivos disponíveis ao registrar uma alteração: ADMISSAO fica de fora porque é
 * o motivo da versão inicial, criada junto com o contrato (o backend recusa).
 */
export const CONTRACT_CHANGE_REASONS_SELECTABLE: DpContractChangeReason[] = [
  "REAJUSTE",
  "PROMOCAO",
  "TRANSFERENCIA",
  "ALTERACAO_JORNADA",
  "ALTERACAO_CARGO",
  "OUTRO",
];

export const UF_OPTIONS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

export const ALLOWANCE_BASE_LABELS: Record<DpAllowanceBase, string> = {
  SALARIO_MINIMO: "Salário mínimo vigente",
  SALARIO_CONTRATUAL: "Salário contratual",
  VALOR_INFORMADO: "Valor informado",
};

/** Rótulo curto, para a linha da timeline ("20% s/ salário mínimo"). */
export const ALLOWANCE_BASE_SHORT_LABELS: Record<DpAllowanceBase, string> = {
  SALARIO_MINIMO: "salário mínimo",
  SALARIO_CONTRATUAL: "salário contratual",
  VALOR_INFORMADO: "valor informado",
};

export const LEGAL_PARAMETER_TYPE_LABELS: Record<DpLegalParameterType, string> = {
  SALARIO_MINIMO: "Salário mínimo",
  SALARIO_FAMILIA_TETO: "Salário-família — teto de remuneração",
  SALARIO_FAMILIA_COTA: "Salário-família — cota por filho",
};

export const LEGAL_PARAMETER_TYPES: DpLegalParameterType[] = [
  "SALARIO_MINIMO",
  "SALARIO_FAMILIA_TETO",
  "SALARIO_FAMILIA_COTA",
];

/** Motivos devolvidos pelo backend ao conferir o salário-família de um dependente. */
export type SalarioFamiliaReason =
  | "PARENTESCO_NAO_ELEGIVEL"
  | "IDADE_14_OU_MAIS"
  | "REMUNERACAO_ACIMA_DO_TETO"
  | "TETO_NAO_CADASTRADO"
  | "REMUNERACAO_INDEFINIDA"
  | "SEM_CONTRATO_VIGENTE";

export const SALARIO_FAMILIA_REASON_LABELS: Record<SalarioFamiliaReason, string> = {
  PARENTESCO_NAO_ELEGIVEL: "parentesco não dá direito",
  IDADE_14_OU_MAIS: "tem 14 anos ou mais",
  REMUNERACAO_ACIMA_DO_TETO: "remuneração acima do teto",
  TETO_NAO_CADASTRADO: "teto não cadastrado",
  REMUNERACAO_INDEFINIDA: "remuneração indefinida (salário mínimo não cadastrado)",
  SEM_CONTRATO_VIGENTE: "sem contrato vigente",
};

/** Participação máxima do empregado no VT (Lei 7.418/85) — espelha o backend. */
export const MAX_TRANSPORT_VOUCHER_EMPLOYEE_PCT = 6;

// ── Recolhimentos de FGTS ───────────────────────────────────────────────────

export type DpFgtsDocumentType = "GUIA_UNICA" | "GUIAS_SEPARADAS";
export type DpFgtsPaymentStatus = "PAGO" | "PENDENTE" | "ATRASADO";

export const FGTS_DOCUMENT_TYPE_LABELS: Record<DpFgtsDocumentType, string> = {
  GUIA_UNICA: "Guia única",
  GUIAS_SEPARADAS: "Guias separadas",
};

export const FGTS_PAYMENT_STATUS_LABELS: Record<DpFgtsPaymentStatus, string> = {
  PAGO: "Pago",
  PENDENTE: "Pendente",
  ATRASADO: "Atrasado",
};

export const FGTS_PAYMENT_STATUS_BADGE: Record<DpFgtsPaymentStatus, string> = {
  PAGO: "dpBadge--green",
  PENDENTE: "dpBadge--orange",
  ATRASADO: "dpBadge--red",
};

// ── Recolhimentos de INSS ───────────────────────────────────────────────────

/** Mesma situação do FGTS: a regra é compartilhada no backend. */
export type DpInssPaymentStatus = DpFgtsPaymentStatus;
export const INSS_PAYMENT_STATUS_LABELS = FGTS_PAYMENT_STATUS_LABELS;
export const INSS_PAYMENT_STATUS_BADGE = FGTS_PAYMENT_STATUS_BADGE;

/** Competência do 13º salário no campo `month`. */
export const INSS_THIRTEENTH_MONTH = 13;
